import { api, ApiError } from './client'
import { useAuthStore } from '../store/auth'
import { localDateString } from '../utils/dates'

export interface AttachmentInfo {
  id: string
  filename: string
  mimetype: string
  content_size: number
}

export interface MediaInfo {
  id: string
  filename: string
  mimetype: string
  content_size: number
  url: string
}

export interface MediaRefEntry {
  id: string
  name: string | null
}

export interface MediaListItem {
  id: string
  filename: string
  mimetype: string
  content_size: number
  url: string
  created_at: string
  referenced_in: MediaRefEntry[]
}

export interface MediaListResponse {
  items: MediaListItem[]
  storage_used: number
  storage_quota: number
  storage_tier: string
}

export interface Entry {
  id: string
  user_id: string
  username: string
  name: string
  mimetype: string
  privacy: number
  allow_replies: boolean
  reply_to_entry_id: string | null
  content: string | null
  version_num: number | null
  signature: string | null
  signature_valid: boolean | null
  public_key_pem: string | null
  color_theme: Record<string, string> | null
  tags: { name: string; tagger_username: string }[]
  attachments: AttachmentInfo[]
  avatar_entry_id: string | null
  created_at: string
  updated_at: string
  archived_at: string | null
  entry_type: string
  todo_status: string | null
  todo_due_date: string | null
  todo_assignee_username: string | null
}

export interface EntryCreateRequest {
  name?: string
  content: string
  mimetype?: string
  privacy?: number
  allow_replies?: boolean
  reply_to_entry_id?: string
  color_theme?: Record<string, string>
  entry_type?: string
  todo_status?: string
  todo_due_date?: string
  todo_assignee_username?: string
}

export interface EntryUpdateRequest {
  name?: string
  content?: string
  privacy?: number
  allow_replies?: boolean
  color_theme?: Record<string, string> | null
  todo_due_date?: string | null
}

export async function createEntry(body: EntryCreateRequest): Promise<Entry> {
  return api.post<Entry>('/entries', { ...body, local_date: localDateString() })
}

export async function getEntry(entryId: string): Promise<Entry> {
  return api.get<Entry>(`/entries/${encodeURIComponent(entryId)}`)
}

export async function updateEntry(entryId: string, body: EntryUpdateRequest): Promise<Entry> {
  return api.patch<Entry>(`/entries/${encodeURIComponent(entryId)}`, body)
}

export async function deleteEntry(entryId: string): Promise<void> {
  return api.delete(`/entries/${encodeURIComponent(entryId)}`)
}

export async function listVersions(entryId: string): Promise<unknown[]> {
  return api.get(`/entries/${encodeURIComponent(entryId)}/versions`)
}

export async function listReplies(entryId: string): Promise<Entry[]> {
  return api.get<Entry[]>(`/entries/${encodeURIComponent(entryId)}/replies`)
}

export async function createReply(parentId: string, body: EntryCreateRequest): Promise<Entry> {
  return api.post<Entry>(`/entries/${encodeURIComponent(parentId)}/replies`, {
    ...body,
    local_date: localDateString(),
  })
}

export async function addTag(entryId: string, tagName: string): Promise<void> {
  return api.post(`/entries/${encodeURIComponent(entryId)}/tags?tag_name=${encodeURIComponent(tagName)}`)
}

export async function removeTag(entryId: string, tagName: string): Promise<void> {
  return api.delete(`/entries/${encodeURIComponent(entryId)}/tags/${encodeURIComponent(tagName)}`)
}

export async function pinEntry(entryId: string): Promise<void> {
  return api.post(`/entries/${encodeURIComponent(entryId)}/pin`)
}

export async function unpinEntry(entryId: string): Promise<void> {
  return api.delete(`/entries/${encodeURIComponent(entryId)}/pin`)
}

export async function hideEntry(entryId: string): Promise<void> {
  return api.post(`/entries/${encodeURIComponent(entryId)}/hide`)
}

export async function archiveEntry(entryId: string): Promise<Entry> {
  return api.post<Entry>(`/entries/${encodeURIComponent(entryId)}/archive`)
}

export async function unarchiveEntry(entryId: string): Promise<Entry> {
  return api.post<Entry>(`/entries/${encodeURIComponent(entryId)}/unarchive`)
}

export async function listArchivedEntries(): Promise<Entry[]> {
  return api.get<Entry[]>('/entries/archive')
}

export async function listParents(entryId: string): Promise<Entry[]> {
  return api.get<Entry[]>(`/entries/${encodeURIComponent(entryId)}/parents`)
}

export async function listTodos(username: string): Promise<Entry[]> {
  return api.get<Entry[]>(`/users/${encodeURIComponent(username)}/todos`)
}

export async function updateTodoStatus(entryId: string, todoStatus: string): Promise<Entry> {
  return api.patch<Entry>(`/entries/${encodeURIComponent(entryId)}`, { todo_status: todoStatus })
}

export async function updateTodoAssignee(entryId: string, assigneeUsername: string | null): Promise<Entry> {
  return api.patch<Entry>(`/entries/${encodeURIComponent(entryId)}`, { todo_assignee_username: assigneeUsername })
}

export async function updateTodoDueDate(entryId: string, dueDate: string | null): Promise<Entry> {
  return api.patch<Entry>(`/entries/${encodeURIComponent(entryId)}`, { todo_due_date: dueDate })
}

export async function getDailyFeed(username: string, date?: string): Promise<Entry[]> {
  const query = date ? `?date=${date}` : ''
  return api.get<Entry[]>(`/users/${username}/feed/daily${query}`)
}

export async function getFeedDates(username: string): Promise<string[]> {
  return api.get<string[]>(`/users/${username}/feed/dates`)
}

export async function getFeed(limit = 50, offset = 0): Promise<Entry[]> {
  return api.get<Entry[]>(`/feed?limit=${limit}&offset=${offset}`)
}

export async function getPublicFeed(limit = 50, offset = 0): Promise<Entry[]> {
  return api.get<Entry[]>(`/feed/public?limit=${limit}&offset=${offset}`)
}

export async function uploadEntry(
  file: File,
  name?: string,
  privacy: number = 0,
  colorTheme?: Record<string, string>,
): Promise<Entry> {
  const token = useAuthStore.getState().token
  const form = new FormData()
  form.append('file', file)
  if (name) form.append('name', name)
  form.append('privacy', String(privacy))
  form.append('local_date', localDateString())
  if (colorTheme) form.append('color_theme', JSON.stringify(colorTheme))
  const resp = await fetch('/api/v1/entries/upload', {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }))
    throw new ApiError(resp.status, detail?.detail ?? resp.statusText)
  }
  return resp.json()
}

export async function downloadEntry(entryId: string, filename: string): Promise<void> {
  const token = useAuthStore.getState().token
  const resp = await fetch(`/api/v1/entries/${encodeURIComponent(entryId)}/download`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!resp.ok) throw new ApiError(resp.status, resp.statusText)
  const blob = await resp.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export async function addAttachment(entryId: string, file: File): Promise<AttachmentInfo> {
  const token = useAuthStore.getState().token
  const form = new FormData()
  form.append('file', file)
  const resp = await fetch(`/api/v1/entries/${encodeURIComponent(entryId)}/attachments`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }))
    throw new ApiError(resp.status, detail?.detail ?? resp.statusText)
  }
  return resp.json()
}

export async function deleteAttachment(entryId: string, attachmentId: string): Promise<void> {
  return api.delete(`/entries/${encodeURIComponent(entryId)}/attachments/${encodeURIComponent(attachmentId)}`)
}

export async function uploadUserMedia(username: string, file: File): Promise<MediaInfo> {
  const token = useAuthStore.getState().token
  const form = new FormData()
  form.append('file', file)
  const resp = await fetch(`/api/v1/users/${encodeURIComponent(username)}/media`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: form,
  })
  if (!resp.ok) {
    const detail = await resp.json().catch(() => ({ detail: resp.statusText }))
    throw new ApiError(resp.status, detail?.detail ?? resp.statusText)
  }
  return resp.json()
}

export async function deleteUserMedia(username: string, mediaId: string): Promise<void> {
  return api.delete(`/users/${encodeURIComponent(username)}/media/${encodeURIComponent(mediaId)}`)
}

export async function listUserMedia(username: string): Promise<MediaListResponse> {
  return api.get<MediaListResponse>(`/users/${encodeURIComponent(username)}/media`)
}

export async function downloadAttachment(entryId: string, attachmentId: string, filename: string): Promise<void> {
  const token = useAuthStore.getState().token
  const resp = await fetch(
    `/api/v1/entries/${encodeURIComponent(entryId)}/attachments/${encodeURIComponent(attachmentId)}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {} },
  )
  if (!resp.ok) throw new ApiError(resp.status, resp.statusText)
  const blob = await resp.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
